import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  passwordRequirementsText,
  validatePassword,
} from '@rivethealth/core/password-requirements';
import jwtDecode from 'jwt-decode';
import { catchError, tap } from 'rxjs';
import config from '../../admin/config';
import { PasswordService } from '../../api/password.service';
import { HookDirective } from '../../common/core/hook';
import { useEffect, useState, useTriggerLoad } from '../../common/core/hooks';
import { emailIsValid } from '../../common/input/email';
import { StringParam } from '../../common/state/route';
import { RouteService, useQueryParam } from '../../common/state/route.service';

@Component({
  host: { class: 'rv-grid xlarge outer' },
  selector: 'change-password',
  styleUrls: ['./change-password.component.scss'],
  templateUrl: './change-password.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangePasswordComponent extends HookDirective {
  constructor(
    private readonly routeService: RouteService,
    private readonly router: Router,
    private readonly passwordService: PasswordService,
    readonly _cdr: ChangeDetectorRef,
  ) {
    super(_cdr);
  }

  passwordRequirementsText = passwordRequirementsText;

  get _() {
    const [envIsZelis] = useState<boolean>(!!config.envIsZelis);

    const [emailQueryParam] = useQueryParam(
      this.routeService,
      new StringParam('email'),
    );
    const [email, setEmail] = useState(emailQueryParam ?? '');
    const [emailHasIssue, setEmailHasIssue] = useState(false);

    const [tokenQueryParam] = useQueryParam(
      this.routeService,
      new StringParam('token'),
    );
    const token = tokenQueryParam ?? '';

    const [password, setPassword] = useState<string>('');
    const [passwordHasIssue, setPasswordHasIssue] = useState(false);
    const [repeatPassword, setRepeatPassword] = useState<string>('');
    const [repeatPasswordHasIssue, setRepeatPasswordHasIssue] = useState(false);

    const [showPassword, setShowPassword] = useState<boolean>(false);

    const [clientSideError, setClientSideError] = useState<string>('');

    const [submitted, setSubmitted] = useState<boolean>(false);
    const [submittingError, setSubmittingError] = useState<string>('');

    const [needToResend, setNeedToResend] = useState<
      false | { explanation: string }
    >(false);

    useEffect(() => {
      if (!emailQueryParam || !tokenQueryParam) {
        setNeedToResend({ explanation: `Your reset link is missing a token.` });
        return;
      }
      try {
        const expiryUnixMillis = jwtDecode<{ exp: number }>(token).exp * 1000;
        // 120-second margin so it (hopefully) doesn't expire while they're
        // on this page
        if (expiryUnixMillis < Date.now() + 120_000) {
          setNeedToResend({ explanation: 'Your reset link has expired.' });
        }
      } catch (e) {
        setNeedToResend({ explanation: 'Your reset link is incomplete.' });
      }
    }, [emailQueryParam, tokenQueryParam]);

    function validateAndSubmit() {
      clearAllErrors();
      if (password.length === 0) {
        setPasswordHasIssue(true);
        setClientSideError('Please enter password.');
      } else if (validatePassword(password).error) {
        setPasswordHasIssue(true);
        setClientSideError(
          'Password does not meet requirements. Please adjust it and try again.',
        );
      } else if (repeatPassword.length === 0) {
        setRepeatPasswordHasIssue(true);
        setClientSideError('Please enter password again.');
      } else if (password !== repeatPassword) {
        setPasswordHasIssue(true);
        setRepeatPasswordHasIssue(true);
        setClientSideError(
          'Passwords do not match. Please make sure they match and try again.',
        );
      } else {
        doSubmit();
      }
    }

    function clearAllErrors() {
      setEmailHasIssue(false);
      setPasswordHasIssue(false);
      setRepeatPasswordHasIssue(false);
      setClientSideError('');
      setSubmittingError('');
    }

    function handleResetError(e: any): never {
      if (e instanceof HttpErrorResponse) {
        switch (e.status) {
          case 403:
          case 401:
            setNeedToResend({
              explanation: `Your reset link has expired.`,
            });
            break;
          default:
            setSubmittingError(
              e.error ||
                e.message ||
                "We're having issues. Please cancel and try again, or contact support.",
            );
        }
      } else {
        setSubmittingError(
          `Unusual error occurred. Please cancel and try again, or contact support.`,
        );
      }
      throw e;
    }

    const [submitLoad, doSubmit] = useTriggerLoad(() => {
      return this.passwordService.resetPassword(email, password, token).pipe(
        catchError(handleResetError),
        tap(() => {
          setSubmitted(true);
        }),
      );
    });

    function validateAndResend() {
      clearAllErrors();
      if (email.length === 0) {
        setEmailHasIssue(true);
        setClientSideError('Please enter email address.');
      } else if (!emailIsValid(email)) {
        setEmailHasIssue(true);
        setClientSideError(
          'This does not appear to be a valid email address. Please make sure it is correct and try again.',
        );
      } else {
        doResend();
      }
    }

    const [resendLoad, doResend] = useTriggerLoad(() => {
      return this.passwordService.sendResetEmail(email).pipe(
        catchError((e: any) => {
          setSubmittingError(
            "We're having issues. Please cancel and try again, or contact support.",
          );
          throw e;
        }),
        tap(() => {
          this.router.navigate(['login', 'sent'], {
            queryParams: {
              email: email,
            },
            replaceUrl: true,
          });
        }),
      );
    });

    return {
      envIsZelis,
      email,
      setEmail,
      emailHasIssue,
      password,
      setPassword,
      repeatPassword,
      setRepeatPassword,
      showPassword,
      setShowPassword,
      passwordHasIssue,
      repeatPasswordHasIssue,
      submitted,
      submitLoad,
      submittingError,
      needToResend,
      resendLoad,
      clientSideError,
      validateAndSubmit,
      clearAllErrors,
      validateAndResend,
    };
  }

  openIntercom() {
    Intercom('show');
  }
}
